import { CdkTrapFocus, FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import {
  BasePortalOutlet,
  CdkPortalOutlet,
  ComponentPortal,
  TemplatePortal,
} from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { take } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';

import { ScrollableDirective } from '../../core/overlay/scroll/scrollable.directive';
import { DialogCloseComponent } from '../dialog-close/dialog-close.component';
import { DialogConfig } from '../dialog-config';
import { DialogContentAlreadyAttachedError } from '../dialog-errors';
import { OVERLAY_LEVEL } from '../providers';

/** Possible states for the dialog container animation. */
export type DialogContainerAnimationState = 'void' | 'enter' | 'exit' | 'exit-start';

/**
 * Internal component that wraps user-provided dialog content.
 */
@Component({
  selector: 'rt-dialog-container',
  templateUrl: './dialog-container.component.html',
  styleUrls: ['./dialog-container.component.less'],
  host: {
    '[attr.role]': 'dialogConfig.role',
    '[class.has-padding]': 'hasPadding',
    '[class.has-padding-bottom]': 'hasPaddingBottom',
    '[class.is-mobile]': 'dialogConfig.mobile',
  },
  standalone: true,
  imports: [
    DialogCloseComponent,
    NgTemplateOutlet,
    ScrollableDirective,
    CdkTrapFocus,
    CdkPortalOutlet,
  ],
})
export class DialogContainerComponent extends BasePortalOutlet implements OnDestroy {
  public readonly overlayLevel = inject(OVERLAY_LEVEL);
  public dialogCloseVisible: boolean;

  @Input() public mobileViewTitle: string | undefined;
  @Input() public hasPadding: boolean = true;
  @Input() public hasPaddingBottom: boolean = true;
  @Input() public dialogConfig: DialogConfig;

  @Output()
  public animationStateChange: EventEmitter<DialogContainerAnimationState> = new EventEmitter(true);

  @ViewChild(ScrollableDirective, { static: true }) public scrollable: ScrollableDirective;

  /** The portal host inside of this container into which the dialog content will be loaded. */
  @ViewChild(CdkPortalOutlet, { static: false }) private portalOutlet: CdkPortalOutlet;

  /** The class that traps and manages focus within the dialog. */
  private focusTrap: FocusTrap;

  /** Element that was focused before the dialog was opened. Save this to restore upon close. */
  private elementFocusedBeforeDialogWasOpened: HTMLElement | null = null;

  /** State of the dialog animation. */
  private animationState: DialogContainerAnimationState = 'enter';

  constructor(
    private elementRef: ElementRef,
    private focusTrapFactory: FocusTrapFactory,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.dialogCloseVisible = !this.dialogConfig.disableClose && this.dialogConfig.showCloseButton;
    this.trapFocus();
  }

  public ngOnDestroy(): void {
    // When the dialog is destroyed, return focus to the element that originally had it before
    // the dialog was opened. Wait for the DOM to finish settling before changing the focus so
    // that it doesn't end up back on the <body>. Also note that we need the extra check, because
    // IE can set the `activeElement` to null in some cases.
    const toFocus = this.elementFocusedBeforeDialogWasOpened;
    if (toFocus && toFocus.focus) {
      toFocus.focus();
    }
    if (this.animationState !== 'exit') {
      this.animationState = 'exit';
      this.animationStateChange.emit('exit');
    }
    this.animationStateChange.complete();

    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
  }

  public attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    if (this.portalOutlet.hasAttached()) {
      throw new DialogContentAlreadyAttachedError();
    }

    return this.portalOutlet.attachComponentPortal(portal);
  }

  public attachTemplatePortal<T>(portal: TemplatePortal<T>): EmbeddedViewRef<T> {
    if (this.portalOutlet.hasAttached()) {
      throw new DialogContentAlreadyAttachedError();
    }

    return this.portalOutlet.attachTemplatePortal(portal);
  }

  // refreshing component from outside
  public detectChanges(): void {
    this.cdRef.detectChanges();
  }

  public close(): void {
    this.dialogConfig
      .closeGuard()
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.animationState = 'exit';
        // this._onAnimationStateChange.emit('exit-start');
        this.animationStateChange.emit('exit');
      });
  }

  /**
   * Moves the focus inside the focus trap.
   */
  private trapFocus(): void {
    if (!this.focusTrap) {
      this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
    }

    // If were to attempt to focus immediately, then the content of the dialog would not yet be
    // ready in instances where change detection has to run first. To deal with this, we simply
    // wait for the microtask queue to be empty.
    this.elementFocusedBeforeDialogWasOpened = <HTMLElement>document.activeElement;
    // No idea why we need this
    // this._focusTrap.focusFirstTabbableElementWhenReady();
  }
}
